/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useState, useEffect, useRef } from 'react';
import {
  Drawer, Avatar, Input, Button, Typography, theme, Spin,
} from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { StoreContext } from '../store/index';
import { listenToMessages, sendChatMessage, markConversationAsRead } from './api';

const formatMsgTime = timestamp => {
  if (!timestamp?.toDate) return '';
  return timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const ChatDrawer = ({ conversationId, recipientUid, recipientNickname, recipientPhotoURL, recipientToken, visible, onClose }) => {
  const store = useContext(StoreContext);
  const { user: [user] } = store;
  const { token } = theme.useToken();
  const { formatMessage } = useIntl();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!conversationId || !visible) return undefined;
    setLoading(true);
    const unsub = listenToMessages(conversationId, msgs => {
      setMessages(msgs);
      setLoading(false);
    });
    return () => unsub();
  }, [conversationId, visible]);

  useEffect(() => {
    if (!conversationId || !visible || !user?.uid) return;
    markConversationAsRead(conversationId, user.uid).catch(() => {});
  }, [conversationId, visible, user?.uid]);

  useEffect(() => {
    if (messages.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (!sending) {
      inputRef.current?.focus();
    }
  }, [sending]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setText('');
    try {
      await sendChatMessage(
        conversationId,
        { uid: user.uid, nickname: user.nickname },
        trimmed,
        recipientUid,
        recipientToken,
      );
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar src={recipientPhotoURL} size={32}>
            {recipientNickname?.[0]?.toUpperCase()}
          </Avatar>
          <Typography.Text strong style={{ fontSize: 14 }}>
            {recipientNickname}
          </Typography.Text>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      zIndex={1100}
      styles={{ body: { display: 'flex', flexDirection: 'column', padding: 0, height: '100%' } }}
    >
      {/* Messages area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spin />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: token.colorTextTertiary }}>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {formatMessage({ id: 'chat.no.messages' })}
            </Typography.Text>
          </div>
        )}
        {!loading && messages.length > 0 && messages.map(msg => {
            const isMe = msg.senderId === user?.uid;
            return (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  justifyContent: isMe ? 'flex-end' : 'flex-start',
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    maxWidth: '72%',
                    padding: '8px 12px',
                    borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: isMe ? token.colorPrimary : token.colorFillSecondary,
                    color: isMe ? token.colorBgContainer : token.colorText,
                  }}
                >
                  <div style={{ fontSize: 14, wordBreak: 'break-word' }}>{msg.text}</div>
                  <div style={{
                    fontSize: 10,
                    marginTop: 3,
                    opacity: 0.7,
                    textAlign: isMe ? 'right' : 'left',
                  }}>
                    {formatMsgTime(msg.createdAt)}
                  </div>
                </div>
              </div>
            );
          })}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div style={{
        padding: '10px 12px',
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        display: 'flex',
        gap: 8,
        background: token.colorBgContainer,
      }}>
        <Input
          ref={inputRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={formatMessage({ id: 'chat.placeholder' })}
          disabled={sending}
          style={{ flex: 1 }}
          maxLength={500}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={sending}
          disabled={!text.trim()}
        />
      </div>
    </Drawer>
  );
};

export default ChatDrawer;
